import React, { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Plus, ArrowUp, ArrowDown, Pencil, Check, X, GripVertical, Trash2 } from "lucide-react";
import { toast } from "sonner";

const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

export const ScheduleRolesManager = ({ onChange }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");

  const load = () =>
    api.get("/schedule-roles").then((r) => { setRoles(r.data); setLoading(false); if (onChange) onChange(r.data); }).catch(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!newName.trim()) { toast.error("Enter a role name"); return; }
    setBusy(true);
    try {
      await api.post("/schedule-roles", { name: newName.trim(), short_code: newCode.trim() });
      setNewName(""); setNewCode("");
      toast.success("Schedule role added");
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startEdit = (r) => { setEditId(r.id); setEditName(r.name); setEditCode(r.short_code || ""); };
  const cancelEdit = () => { setEditId(null); setEditName(""); setEditCode(""); };

  const saveEdit = async (r) => {
    setBusy(true);
    try {
      await api.patch(`/schedule-roles/${r.id}`, { name: editName.trim(), short_code: editCode.trim() });
      toast.success("Schedule role updated");
      cancelEdit();
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (r, active) => {
    const next = roles.map((x) => (x.id === r.id ? { ...x, active } : x));
    setRoles(next);
    try {
      await api.patch(`/schedule-roles/${r.id}`, { active });
      if (onChange) onChange(next);
    } catch (err) {
      setRoles((prev) => prev.map((x) => (x.id === r.id ? { ...x, active: !active } : x)));
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/schedule-roles/${r.id}`);
      toast.success("Schedule role deleted");
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= roles.length) return;
    const next = [...roles];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRoles(next);
    try {
      const { data } = await api.post("/schedule-roles/reorder", { ids: next.map((r) => r.id) });
      setRoles(data);
      if (onChange) onChange(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); load(); }
  };

  return (
    <div data-testid="schedule-roles-manager">
      <p className="text-xs text-slate-500">
        Operational roles for the schedule (e.g. Supervisor, Dispatcher, Trainee). These are separate from
        WatchRelay account permissions and never change what a user can access.
      </p>

      {loading ? (
        <div className="mt-4 h-24 animate-pulse rounded bg-slate-100" />
      ) : (
        <div className="mt-4 space-y-2" data-testid="schedule-roles-list">
          {roles.map((r, idx) => (
            <div key={r.id} data-testid={`schedule-role-${r.id}`}
              className={`flex flex-wrap items-center gap-2 rounded-md border p-2.5 ${r.active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/70"}`}>
              <div className="flex flex-col">
                <button type="button" data-testid={`role-up-${r.id}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                  className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" data-testid={`role-down-${r.id}`} onClick={() => move(idx, 1)} disabled={idx === roles.length - 1}
                  className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
              <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />

              {editId === r.id ? (
                <>
                  <Input data-testid={`role-edit-name-${r.id}`} value={editName} onChange={(e) => setEditName(e.target.value)}
                    className="h-9 flex-1 min-w-[140px]" placeholder="Role name" />
                  <Input data-testid={`role-edit-code-${r.id}`} value={editCode} onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                    maxLength={6} className="h-9 w-20 uppercase" placeholder="CODE" />
                  <Button type="button" size="sm" data-testid={`role-save-${r.id}`} onClick={() => saveEdit(r)} disabled={busy}
                    className="h-9 gap-1 bg-blue-600 hover:bg-blue-700 text-white"><Check className="h-4 w-4" /></Button>
                  <Button type="button" size="sm" variant="outline" data-testid={`role-cancel-${r.id}`} onClick={cancelEdit} className="h-9 gap-1"><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <>
                  <span className="flex-1 min-w-[120px] text-sm font-semibold text-slate-900">
                    {r.name}
                    {r.is_default && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">Default</span>}
                  </span>
                  <span className="rounded bg-blue-50 px-2 py-0.5 text-[11px] font-bold tracking-wide text-blue-700">{r.short_code}</span>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Switch data-testid={`role-active-${r.id}`} checked={!!r.active} onCheckedChange={(v) => toggleActive(r, v)} />
                    {r.active ? "Active" : "Off"}
                  </label>
                  <Button type="button" size="sm" variant="ghost" data-testid={`role-edit-${r.id}`} onClick={() => startEdit(r)}
                    className="h-8 gap-1 text-slate-500 hover:text-slate-900"><Pencil className="h-3.5 w-3.5" /></Button>
                  {!r.is_default && (
                    <Button type="button" size="sm" variant="ghost" data-testid={`role-delete-${r.id}`} onClick={() => remove(r)}
                      className="h-8 gap-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
        <div className="flex-1 min-w-[160px]">
          <Label className={labelCls}>New role name</Label>
          <Input data-testid="new-role-name" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Communications Supervisor" className="mt-1.5 h-9" />
        </div>
        <div>
          <Label className={labelCls}>Short code</Label>
          <Input data-testid="new-role-code" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            maxLength={6} placeholder="auto" className="mt-1.5 h-9 w-24 uppercase" />
        </div>
        <Button type="button" data-testid="add-role-button" onClick={add} disabled={busy}
          className="h-9 gap-1 bg-blue-600 hover:bg-blue-700 text-white"><Plus className="h-4 w-4" /> Add role</Button>
      </div>
    </div>
  );
};

const Label = ({ className, children }) => <p className={className}>{children}</p>;

export default ScheduleRolesManager;
